import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    Collapse,
    Grid,
    Divider,
    Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import BasicModalDialogFacturas from '../Componentes/BasicModalDialogFacturas';
import dayjs from 'dayjs';

const InvoiceCard = ({ invoice }) => {
    const { t } = useTranslation("global");
    const [expanded, setExpanded] = useState(false);

    const getStatusColor = (status) => {
        if (!status) return 'default';
        if (status === 'Pendiente de pago - Sin vencer') return 'success';
        if (status === 'Pendiente de pago - Vencida') return 'error';
        if (status === 'Pendiente de pago') return 'warning';

        // Fallback para otros casos
        const s = status.toLowerCase();
        if (s.includes('pago') || s.includes('pagad')) return 'success';
        if (s.includes('pend')) return 'warning';
        if (s.includes('venc')) return 'error';
        return 'default';
    };

    const formatCurrency = (value) => {
        return (Number(value * -1)).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDate = (date) => {
        return dayjs(date).add(1, 'day').format('DD/MM/YYYY');
    };

    // El estado ya viene calculado del servicio con strings específicos
    const statusColor = getStatusColor(invoice.estado);
    const isOverdue = invoice.estado === 'Pendiente de pago - vencida';

    return (
        <Card
            elevation={2}
            sx={{
                mb: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                },
                borderLeft: `4px solid ${statusColor === 'success' ? '#22C55E' :
                    statusColor === 'warning' ? '#F59E0B' :
                        statusColor === 'error' ? '#EF4444' : '#9CA3AF'
                    }`
            }}
        >
            <CardContent>
                {/* Header compacto */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <DescriptionIcon sx={{ color: '#BD1F34', fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937' }}>
                                {invoice.idFactura}
                            </Typography>
                            <Chip
                                label={invoice.estado}
                                color={statusColor}
                                size="small"
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16 }} />
                            {t("facturas.card.documento")}: {formatDate(invoice.fechaDocumento)}
                        </Typography>

                        <Typography variant="body2" color={isOverdue ? 'error' : 'text.secondary'} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16 }} />
                            {t("facturas.card.vencimiento")}: {formatDate(invoice.fechaVencimiento)}
                            {isOverdue && ` (${t("facturas.card.vencida")})`}
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            backgroundColor: '#F3F4F6',
                            '&:hover': { backgroundColor: '#E5E7EB' }
                        }}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                {/* Info principal */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t("facturas.card.tipo")}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {invoice.tipodedocumento}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t("facturas.card.importe")}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#BD1F34' }}>
                            {formatCurrency(invoice.importePendiente)} {invoice.moneda}
                        </Typography>
                    </Grid>
                </Grid>

                {/* Detalles expandibles */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("facturas.card.condicion")}</Typography>
                            <Typography variant="body2">{invoice.condicion}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">{t("facturas.card.moneda")}</Typography>
                            <Typography variant="body2">{invoice.moneda}</Typography>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                        <BasicModalDialogFacturas
                            factura={invoice.idFactura}
                            proveedor={localStorage.Tproveedor}
                            text={""}
                            boton={t("facturas.card.contactanos") || "Contáctanos"}
                            tipo={"Facturas"}
                        />
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default InvoiceCard;
